import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMediaQuery } from '../../state/useMediaQuery';
import './DataTable.css';

/** Where a table with a `phone` layout stops being a table: the same width
 *  at which `DataTable.css` gives up on columns for every other table. */
export const DATA_TABLE_PHONE_QUERY = '(max-width: 640px)';

/** A trouble tint that stays on a row whatever the sort. */
export type DataTableRowTone = 'critical' | 'warning';

/** How a table draws on a phone: no columns, one tappable block per row. */
export interface DataTablePhoneLayout<Row> {
  /** Name + chip, one fact line, one small grey line. */
  renderRow: (row: Row) => ReactNode;
  /** The row holds controls of its own (buttons, a link), so the block
   *  cannot itself be a button: a button inside a button is invalid, and a
   *  press on one would also open the row. The block is then plain, and
   *  whatever opens the row is the renderer's to draw. */
  rowHoldsControls?: boolean;
  /** The sort sheet's keys, in the order offered, by column id and with
   *  the word to say: a phone has no heading to match ("Sort: Name", where
   *  the table's heading says "Lift"). Each must be a sortable column.
   *  Every sortable column, by its heading, when omitted. */
  sortKeys?: readonly { id: string; label: string }[];
}

export interface DataTableColumn<Row> {
  id: string;
  /** Usually plain text; a column that needs an inline header control
   *  (e.g. a filter select) can pass a node instead. */
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  /** Raw comparable value for sorting — required alongside
   *  `sortable: true`; `cell`'s return is a React node, not something
   *  that can be compared directly.
   *
   *  `null` means NOT KNOWN YET (a value the row is still fetching), as
   *  opposed to known-empty: those rows go after every other row in
   *  EITHER direction, in the order they were handed. Sorted as blank,
   *  they would lead an ascending sort -- the top of the list, the rows
   *  on screen -- and a column that fetches what its on-screen rows lack
   *  would fetch them all, a screenful at a time. */
  sortValue?: (row: Row) => string | number | null;
  /** Plain-text name for this column, when `header` is a node rather
   *  than a string. Used as the `data-label` each cell carries, which
   *  is what a stacked-card layout on a narrow screen reads to say
   *  WHICH value it is showing -- a column of bare values with no
   *  headings is unreadable. Falls back to `header` when that is a
   *  string, and to `id` otherwise. */
  headerLabel?: string;
  /** Clicks anywhere in this column's cells are the cell's own and never
   *  open the row: for a control column (a tick box, a row of buttons)
   *  and for text that is there to be copied. The CELL, not the control
   *  in it: a control is smaller than its cell, so a press in the cell's
   *  padding -- or, on a phone's card, anywhere along its full-width
   *  line -- would otherwise reach the row and open it. */
  stopsRowClick?: boolean;
}

/**
 * Is some text inside `el` selected? The click that ends a drag-select
 * within one cell lands on that cell, and opening the row on it would
 * navigate away from the text being copied. Only a selection inside this
 * row counts, so one left elsewhere on the page never makes a row dead.
 * It cannot help a double-click, whose first click arrives before anything
 * is selected: a cell that is there to be copied sets `stopsRowClick`.
 */
function hasTextSelectedIn(el: HTMLElement): boolean {
  const selection = typeof window.getSelection === 'function' ? window.getSelection() : null;
  if (!selection || selection.isCollapsed) return false;
  return el.contains(selection.anchorNode) || el.contains(selection.focusNode);
}

const stopRowClick = (e: React.MouseEvent) => e.stopPropagation();

function labelOf<Row>(col: DataTableColumn<Row>): string {
  // `!== undefined`, not truthiness: '' is the documented way for a
  // control column to have NO card label (`DataTable.css`,
  // `td[data-label='']`), and a truthiness test sent it on to the
  // fallbacks instead, so a phone's alert card opened on "select" in
  // front of its checkbox and "Actions" in front of its buttons.
  if (col.headerLabel !== undefined) return col.headerLabel;
  return typeof col.header === 'string' ? col.header : col.id;
}

export type SortDirection = 'asc' | 'desc';
export interface DataTableSort {
  columnId: string;
  direction: SortDirection;
}

export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  onRowClick,
  emptyState,
  loading,
  ariaLabel,
  defaultSort,
  highlightRowId,
  onSortChosen,
  isRowClickable,
  fixedLayout = false,
  rowTone,
  phone,
}: {
  /** `table-layout: fixed`. Every column then needs an explicit `width`
   *  (percentages summing to 100), or Chrome crushes the unsized one. */
  fixedLayout?: boolean;
  /** A row's trouble tint. */
  rowTone?: (row: Row) => DataTableRowTone | null;
  /** The phone layout. Without it the stylesheet stacks the columns into
   *  labelled cards. */
  phone?: DataTablePhoneLayout<Row>;
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  /** Which rows `onRowClick` applies to, when not every row has
   *  somewhere to go. A row it refuses is an ordinary row: no pointer,
   *  no tab stop, no handler -- rather than one that looks like a link
   *  and does nothing. Every row when omitted. */
  isRowClickable?: (row: Row) => boolean;
  emptyState?: ReactNode;
  loading?: boolean;
  ariaLabel: string;
  /** Marks one row `data-highlighted`, for arriving at a screen from a
   *  link that names a specific record -- a chat notification, an
   *  email, a support ticket. Styling only: it does not select, sort or
   *  filter, because a link should not silently change what the
   *  operator is looking at, only point inside it. */
  highlightRowId?: string;
  /** The order rows take before anyone touches a header, and the order
   *  a header's third click returns to. Without it the table opens in
   *  the caller's array order — which for a live feed is "whatever the
   *  store happened to return", not something an operator can read. */
  defaultSort?: DataTableSort;
  /** Told when the PERSON picks a sort by pressing a header, with the
   *  sort the press chose (`null` for the caller's order). Not called
   *  for the opening sort, nor for the return to it when the sorted
   *  column goes away. For a caller whose sort values fill in after the
   *  table is up and should be read at the moment of choosing -- see
   *  the Tasks table's Location. */
  onSortChosen?: (sort: DataTableSort | null) => void;
}) {
  const [chosenSort, setSort] = useState<DataTableSort | null>(defaultSort ?? null);
  const narrow = useMediaQuery(DATA_TABLE_PHONE_QUERY);

  // A SORT ON A COLUMN THAT IS NO LONGER SHOWN returns to the default.
  // Columns can now come and go under a sort (a person hiding one on
  // the Tasks table), and a sort nobody can see -- no header carries
  // its arrow -- reads as rows in no particular order. Reset in state,
  // not just overridden here, so showing the column again does not
  // bring back an order the person had already left behind.
  const chosenShown = !chosenSort || columns.some((c) => c.id === chosenSort.columnId);
  const sort = chosenShown ? chosenSort : (defaultSort ?? null);
  useEffect(() => {
    if (!chosenShown) setSort(defaultSort ?? null);
  }, [chosenShown, defaultSort]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.id === sort.columnId);
    if (!col?.sortValue) return rows;
    const getValue = col.sortValue;
    // Unknown values are set aside before sorting, not given a sentinel:
    // `desc` reverses the sorted rows, so a sentinel would land them
    // first in one direction or the other.
    const known: { row: Row; value: string | number }[] = [];
    const unknown: Row[] = [];
    for (const row of rows) {
      const value = getValue(row);
      if (value === null) unknown.push(row);
      else known.push({ row, value });
    }
    known.sort((a, b) => {
      if (a.value < b.value) return -1;
      if (a.value > b.value) return 1;
      return 0;
    });
    if (sort.direction === 'desc') known.reverse();
    return [...known.map((k) => k.row), ...unknown];
  }, [rows, sort, columns]);

  function nextSort(col: DataTableColumn<Row>): DataTableSort | null {
    // From the sort ON SCREEN, which is the default once a chosen sort's
    // column has gone, even before the effect above has caught up.
    const prev = sort;
    if (!prev || prev.columnId !== col.id) return { columnId: col.id, direction: 'asc' };
    if (prev.direction === 'asc') return { columnId: col.id, direction: 'desc' };
    // Third click returns to where the table opened: the caller's
    // default when it named one, natural order otherwise. On the
    // default column itself that would be a click that changes
    // nothing, so there the two directions simply alternate.
    if (defaultSort?.columnId === col.id && defaultSort.direction === 'desc') {
      return { columnId: col.id, direction: 'asc' };
    }
    return defaultSort ?? null;
  }

  function toggleSort(col: DataTableColumn<Row>) {
    if (!col.sortable || !col.sortValue) return;
    const next = nextSort(col);
    setSort(next);
    onSortChosen?.(next);
  }

  if (loading) {
    return (
      <div role="status" aria-live="polite" aria-label={ariaLabel}>
        Loading…
      </div>
    );
  }
  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }
  if (phone && narrow) {
    const sortable = columns.filter((c) => c.sortable && c.sortValue);
    const sortOptions = phone.sortKeys
      ? phone.sortKeys.filter((key) => sortable.some((c) => c.id === key.id))
      : sortable.map((c) => ({ id: c.id, label: labelOf(c) }));
    return (
      <PhoneRows
        ariaLabel={ariaLabel}
        rows={sortedRows}
        getRowId={getRowId}
        renderRow={phone.renderRow}
        rowTone={rowTone}
        highlightRowId={highlightRowId}
        isRowClickable={isRowClickable}
        onRowClick={phone.rowHoldsControls ? undefined : onRowClick}
        sortOptions={sortOptions}
        sort={sort}
        onSort={(next) => {
          setSort(next);
          onSortChosen?.(next);
        }}
      />
    );
  }
  return (
    // Scroll container. The sticky `thead` in DataTable.css only
    // actually sticks if something scrolls, and a bare <table> has no
    // scroll box of its own -- so before this wrapper existed the
    // sticky rule was inert and every table scrolled the whole PAGE
    // instead. Screens opt into internal scrolling by constraining
    // this element's height (see `.nx-page-fill` in section.css);
    // where they don't, it grows naturally and behaves exactly as
    // before.
    <div className="nx-data-table-scroll">
      <table
        className={`nx-data-table${fixedLayout ? ' nx-data-table--fixed' : ''}`}
        aria-label={ariaLabel}
        // THE COLUMN COUNT, FOR CSS. A table is `width: 100%` with no
        // floor, so on a narrow screen an eight-column table squeezed
        // every column to about 38px and word-wrapped each cell into a
        // tower rather than scrolling. How wide it needs to be before
        // that happens depends on how many columns it has, which only
        // this component knows -- so it hands the number to the
        // stylesheet instead of the stylesheet guessing one width for
        // tables of four columns and of nine.
        style={{ '--nx-data-table-cols': columns.length } as React.CSSProperties}
      >
        <thead>
          <tr>
            {columns.map((col) => {
              const isSorted = sort?.columnId === col.id;
              const canSort = Boolean(col.sortable && col.sortValue);
              return (
                <th
                  key={col.id}
                  style={{ width: col.width, textAlign: col.align ?? 'left' }}
                  scope="col"
                  aria-sort={isSorted ? (sort.direction === 'asc' ? 'ascending' : 'descending') : canSort ? 'none' : undefined}
                >
                  {canSort ? (
                    <button
                      type="button"
                      className="nx-data-table__sort-btn"
                      onClick={() => toggleSort(col)}
                      data-testid={`sort-${col.id}`}
                    >
                      {col.header}
                      <span className="nx-data-table__sort-icon" aria-hidden="true">
                        {isSorted ? (sort.direction === 'asc' ? '▲' : '▼') : '⇅'}
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const open = onRowClick && (!isRowClickable || isRowClickable(row)) ? onRowClick : null;
            return (
              <tr
                key={getRowId(row)}
                data-highlighted={
                  highlightRowId && getRowId(row) === highlightRowId ? 'true' : undefined
                }
                data-tone={rowTone?.(row) ?? undefined}
                onClick={
                  open
                    ? (e) => {
                        if (hasTextSelectedIn(e.currentTarget)) return;
                        open(row);
                      }
                    : undefined
                }
                className={open ? 'nx-data-table__row--clickable' : undefined}
                tabIndex={open ? 0 : undefined}
                onKeyDown={
                  open
                    ? (e) => {
                        // Only while the ROW has focus. A key pressed on a
                        // control inside it (a checkbox, a button) bubbles
                        // here too, and handling it would cancel that
                        // control's own Enter or Space and open the row
                        // instead: an Ack pressed from the keyboard would
                        // navigate away and ack nothing.
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          open(row);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((col) => (
                  <td
                    key={col.id}
                    style={{ textAlign: col.align ?? 'left' }}
                    data-label={labelOf(col)}
                    onClick={col.stopsRowClick ? stopRowClick : undefined}
                    className={col.stopsRowClick ? 'nx-data-table__cell--own-clicks' : undefined}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
          </tbody>
      </table>
    </div>
  );
}

/**
 * The phone layout: a list of tappable blocks, and the sort the headings
 * would have given behind a "Sort: Name" button and a sheet. It sorts
 * through the table's own state, so turning a tablet round keeps the order.
 */
function PhoneRows<Row>({
  ariaLabel,
  rows,
  getRowId,
  renderRow,
  rowTone,
  highlightRowId,
  isRowClickable,
  onRowClick,
  sortOptions,
  sort,
  onSort,
}: {
  ariaLabel: string;
  rows: Row[];
  getRowId: (row: Row) => string;
  renderRow: (row: Row) => ReactNode;
  rowTone?: (row: Row) => DataTableRowTone | null;
  highlightRowId?: string;
  isRowClickable?: (row: Row) => boolean;
  onRowClick?: (row: Row) => void;
  sortOptions: readonly { id: string; label: string }[];
  sort: DataTableSort | null;
  onSort: (sort: DataTableSort) => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const current = sortOptions.find((o) => o.id === sort?.columnId);
  useEffect(() => {
    if (!sheetOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setSheetOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen]);
  return (
    <div className="nx-phone-rows">
      {sortOptions.length > 0 && (
        <div className="nx-phone-rows__bar">
          <button
            type="button"
            className="nx-phone-rows__sort"
            aria-haspopup="dialog"
            onClick={() => setSheetOpen(true)}
            data-testid="phone-sort-button"
          >
            Sort: {current?.label ?? 'None'}{' '}
            {sort && current ? (sort.direction === 'asc' ? '▲' : '▼') : '▾'}
          </button>
        </div>
      )}
      <ul className="nx-phone-rows__list" aria-label={ariaLabel}>
        {rows.map((row) => {
          const id = getRowId(row);
          const open = onRowClick && (!isRowClickable || isRowClickable(row)) ? onRowClick : null;
          const tone = rowTone?.(row) ?? undefined;
          const highlighted = highlightRowId === id ? 'true' : undefined;
          return (
            <li key={id} className="nx-phone-rows__item">
              {open ? (
                <button
                  type="button"
                  className="nx-phone-row"
                  data-tone={tone}
                  data-highlighted={highlighted}
                  onClick={() => open(row)}
                >
                  {renderRow(row)}
                </button>
              ) : (
                <div className="nx-phone-row" data-tone={tone} data-highlighted={highlighted}>
                  {renderRow(row)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {sheetOpen && (
        <div className="nx-phone-sort-sheet__scrim" onClick={() => setSheetOpen(false)}>
          <div
            className="nx-phone-sort-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Sort by"
            onClick={(e) => e.stopPropagation()}
            data-testid="phone-sort-sheet"
          >
            <h2 className="nx-phone-sort-sheet__title">Sort by</h2>
            {sortOptions.map((option) => {
              const selected = option.id === sort?.columnId;
              const arrow = selected ? (sort?.direction === 'asc' ? ' ▲' : ' ▼') : '';
              return (
                <button
                  key={option.id}
                  type="button"
                  className="nx-phone-sort-sheet__option"
                  aria-pressed={selected}
                  autoFocus={selected}
                  onClick={() => {
                    onSort({
                      columnId: option.id,
                      // The chosen key again turns the order round.
                      direction: selected && sort?.direction === 'asc' ? 'desc' : 'asc',
                    });
                    setSheetOpen(false);
                  }}
                >
                  {option.label}
                  {arrow}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
